import moment from "moment";
import dateFormats from "app/data/common/dateFormats";
import { downloadCsvFile } from "app/data/common/exportToCsv";
import ZipRoutingProfileService from "app/data/zip-routing-profile/zipRoutingProfileService";
import {
  ROUTE_GUIDE_FIELD_LABELS,
  SERVICE_WEEKDAY_KEYS,
  ZipRoutingProfile,
  ZipRoutingProfileListParams,
  formatStateDisplay,
  getProfileServiceDayCode,
  resolveTerminalDisplayInput,
} from "app/data/zip-routing-profile/models";

export type RouteGuideCsvTerminalLookup = Map<
  string,
  { airportCode?: string; terminalName?: string }
>;

const EXPORT_PAGE_SIZE = 500;

/** ASCII-safe blank for Excel (avoid Unicode em dash mojibake like â€"). */
const CSV_BLANK = "";

const resolveDirect = (row: ZipRoutingProfile): boolean => {
  if (typeof row.direct === "boolean") return row.direct;
  if (row.rateType === "I") return false;
  if (row.rateType === "D") return true;
  return true;
};

const formatCoverage = (row: ZipRoutingProfile): string =>
  row.coverageStatus === "NOT_SERVICED" ? "Not Serviced" : "Serviced";

const formatLastUpdated = (value?: string | null): string => {
  if (!value?.trim()) return CSV_BLANK;
  return (value as any).toUiDateTimeFormat?.() || value;
};

const formatTerminalForCsv = (
  row: ZipRoutingProfile,
  terminalsByNumber?: RouteGuideCsvTerminalLookup
): string => {
  if (row.coverageStatus === "NOT_SERVICED" || row.terminalNumber == null) return CSV_BLANK;
  const input = resolveTerminalDisplayInput(row, terminalsByNumber ?? new Map());
  const parts = [
    input.airportCode?.trim(),
    input.terminalName?.trim(),
    input.terminalNumber != null && input.terminalNumber !== ""
      ? String(input.terminalNumber)
      : undefined,
  ].filter(Boolean) as string[];
  // Use ASCII " - " so Excel does not misread Unicode em dashes
  return parts.length ? parts.join(" - ") : CSV_BLANK;
};

const formatThreePlLaneForCsv = (code?: string | null): string => {
  if (!code?.trim()) return CSV_BLANK;
  if (code === "D") return "Direct";
  if (code === "I") return "Indirect";
  return code;
};

const formatExtensionForCsv = (code?: string | null): string => {
  if (!code?.trim()) return CSV_BLANK;
  if (code === "NEWYORK") return "New York";
  return code.trim();
};

const formatServiceDayForCsv = (code?: string | null): string => {
  const trimmed = code?.trim();
  if (!trimmed) return CSV_BLANK;
  const upper = trimmed.toUpperCase();
  if (upper === "Y" || upper === "I") return upper;
  if (/^[0-9]$/.test(trimmed)) return trimmed;
  return trimmed;
};

/** Fetch every page for the current filters (larger page size for fewer round-trips). */
export const fetchAllRouteGuideProfiles = async (
  filters: Omit<ZipRoutingProfileListParams, "page" | "size">
): Promise<ZipRoutingProfile[]> => {
  const service = ZipRoutingProfileService.getInstance();
  const all: ZipRoutingProfile[] = [];
  let page = 0;

  while (true) {
    const result = await service.getList({
      ...filters,
      page,
      size: EXPORT_PAGE_SIZE,
    });
    all.push(...(result.content ?? []));
    const totalPages = result.totalPages ?? 0;
    if (page + 1 >= totalPages || !(result.content?.length)) break;
    page += 1;
  }

  return all;
};

export const exportRouteGuideToCsv = (
  rows: ZipRoutingProfile[],
  terminalsByNumber?: RouteGuideCsvTerminalLookup
): void => {
  const headers = [
    "ZIP",
    "State",
    "Terminal",
    "Coverage",
    "Service Type",
    ROUTE_GUIDE_FIELD_LABELS.threePlOriginCode,
    ROUTE_GUIDE_FIELD_LABELS.threePlDestCode,
    ROUTE_GUIDE_FIELD_LABELS.extensionCode1,
    ROUTE_GUIDE_FIELD_LABELS.extensionCode2,
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Updated",
  ];

  const csvRows = rows.map((row) => {
    const dayCodes = SERVICE_WEEKDAY_KEYS.map((day) =>
      formatServiceDayForCsv(getProfileServiceDayCode(row, day))
    );

    return [
      row.zipCode || row.zip || "",
      formatStateDisplay(row.state),
      formatTerminalForCsv(row, terminalsByNumber),
      formatCoverage(row),
      resolveDirect(row) ? "Direct" : "Indirect",
      formatThreePlLaneForCsv(row.threePlOriginCode),
      formatThreePlLaneForCsv(row.threePlDestCode),
      formatExtensionForCsv(row.extensionCode1),
      formatExtensionForCsv(row.extensionCode2),
      ...dayCodes,
      formatLastUpdated(row.dateUpdated),
    ];
  });

  downloadCsvFile(
    `route-guide-profiles-${moment().format(dateFormats.uiDate)}.csv`,
    headers,
    csvRows
  );
};
